// @ts-nocheck
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Ticket, Info } from "lucide-react";
import { EventFormData, SpectatorTicket, EventExtra } from "../CreateEventForm";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface ExtrasTabProps {
  formData: EventFormData;
  updateFormData: (data: Partial<EventFormData>) => void;
}

const ExtrasTab: React.FC<ExtrasTabProps> = ({ formData, updateFormData }) => {
  
  const generateQRCode = (): string => {
    return 'qr-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36);
  };

  const addSpectatorTicket = () => {
    const newTicket: SpectatorTicket = {
      id: crypto.randomUUID(),
      name: "",
      price: "", // Change to empty string
      is_active: true,
      thumbnail_url: "",
      qr_code_value: generateQRCode()
    };

    updateFormData({
      spectator_tickets: [...formData.spectator_tickets, newTicket]
    });
  };

  const updateSpectatorTicket = (index: number, updates: Partial<SpectatorTicket>) => {
    const updatedTickets = formData.spectator_tickets.map((ticket, i) => 
      i === index ? { ...ticket, ...updates } : ticket
    );
    updateFormData({ spectator_tickets: updatedTickets });
  };

  const removeSpectatorTicket = (index: number) => {
    const updatedTickets = formData.spectator_tickets.filter((_, i) => i !== index);
    updateFormData({ spectator_tickets: updatedTickets });
  };

  const handleTicketImageUpload = async (index: number, file: File) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (.jpg, .png, .webp)');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `ticket-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      
      console.log('📤 Uploading spectator ticket image:', fileName);

      const { error: uploadError } = await supabase.storage
        .from('event-spectator-tickets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        toast.error('Failed to upload image');
        return;
      }

      console.log('✅ Spectator ticket image uploaded successfully:', fileName);
      
      // Save only the filename to the database
      updateSpectatorTicket(index, { thumbnail_url: fileName });
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('💥 Unexpected error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  const addEventExtra = () => {
    const newExtra: EventExtra = {
      id: crypto.randomUUID(),
      name: "",
      price: "", // Change to empty string
      description: "",
      is_active: true,
      thumbnail_url: ""
    };

    updateFormData({
      event_extras: [...formData.event_extras, newExtra]
    });
  };

  const updateEventExtra = (index: number, updates: Partial<EventExtra>) => {
    const updatedExtras = formData.event_extras.map((extra, i) => 
      i === index ? { ...extra, ...updates } : extra
    );
    updateFormData({ event_extras: updatedExtras });
  };

  const removeEventExtra = (index: number) => {
    const updatedExtras = formData.event_extras.filter((_, i) => i !== index);
    updateFormData({ event_extras: updatedExtras });
  };

  const handleExtraImageUpload = async (index: number, file: File) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (.jpg, .png, .webp)');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `extra-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
      
      console.log('📤 Uploading event extra image:', fileName);

      const { error: uploadError } = await supabase.storage
        .from('event-extras')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        toast.error('Failed to upload image');
        return;
      }

      console.log('✅ Event extra image uploaded successfully:', fileName);
      
      // Save only the filename to the database
      updateEventExtra(index, { thumbnail_url: fileName });
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('💥 Unexpected error uploading image:', error);
      toast.error('Failed to upload image');
    }
  };

  // Helper function to get full URL for preview
  const getImagePreviewUrl = (filename: string | null | undefined, bucketName: string): string => {
    if (!filename) return '';
    // If it's already a full URL, return as is
    if (filename.startsWith('http')) return filename;
    // Construct the full URL for preview
    return `https://pvaumvowzncvrdkkvnqq.supabase.co/storage/v1/object/public/${bucketName}/${filename}`;
  };

  return (
    <div className="space-y-6">
      {/* Ticket Display Text */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Ticket Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="ticket_display_text">Ticket Display Text</Label>
            <Textarea
              id="ticket_display_text"
              value={formData.ticket_display_text}
              onChange={(e) => updateFormData({ ticket_display_text: e.target.value })}
              placeholder="e.g., Additional information about tickets for this event"
              rows={3}
            />
            <p className="text-sm text-muted-foreground mt-1">
              This text will be displayed in the tickets box on the event page.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Spectator Tickets Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Spectator Tickets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.spectator_tickets.map((ticket, index) => (
            <div key={ticket.id} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Ticket {index + 1}</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeSpectatorTicket(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`ticket_name_${index}`}>Ticket Name</Label>
                  <Input
                    id={`ticket_name_${index}`}
                    value={ticket.name}
                    onChange={(e) => updateSpectatorTicket(index, { name: e.target.value })}
                    placeholder="e.g., General Admission"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`ticket_price_${index}`}>Price ($)</Label>
                  <Input
                    id={`ticket_price_${index}`}
                    type="text"
                    value={ticket.price ?? ''}
                    onChange={(e) => updateSpectatorTicket(index, { price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`ticket_thumbnail_${index}`}>Thumbnail Image</Label>
                <Input
                  id={`ticket_thumbnail_${index}`}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleTicketImageUpload(index, file);
                  }}
                />
                {ticket.thumbnail_url && (
                  <div className="mt-2">
                    <img 
                      src={getImagePreviewUrl(ticket.thumbnail_url, 'event-spectator-tickets')} 
                      alt="Ticket thumbnail" 
                      className="w-16 h-16 object-cover rounded border"
                      onError={(e) => {
                        console.error('❌ Failed to load ticket image:', ticket.thumbnail_url);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id={`ticket_active_${index}`}
                  checked={ticket.is_active}
                  onCheckedChange={(checked) => updateSpectatorTicket(index, { is_active: checked })}
                />
                <Label htmlFor={`ticket_active_${index}`}>Active</Label>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addSpectatorTicket}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Spectator Ticket
          </Button>
        </CardContent>
      </Card>

      {/* Event Extras Section */}
      <Card>
        <CardHeader>
          <CardTitle>Event Extras (Merchandise & Add-ons)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {formData.event_extras.map((extra, index) => (
            <div key={extra.id} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Extra {index + 1}</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeEventExtra(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`extra_name_${index}`}>Item Name</Label>
                  <Input
                    id={`extra_name_${index}`}
                    value={extra.name}
                    onChange={(e) => updateEventExtra(index, { name: e.target.value })}
                    placeholder="e.g., Event T-Shirt"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`extra_price_${index}`}>Price ($)</Label>
                  <Input
                    id={`extra_price_${index}`}
                    type="text"
                    value={extra.price ?? ''}
                    onChange={(e) => updateEventExtra(index, { price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor={`extra_description_${index}`}>Description (Optional)</Label>
                <Textarea
                  id={`extra_description_${index}`}
                  value={extra.description}
                  onChange={(e) => updateEventExtra(index, { description: e.target.value })}
                  placeholder="Describe the item..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor={`extra_thumbnail_${index}`}>Thumbnail Image</Label>
                <Input
                  id={`extra_thumbnail_${index}`}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleExtraImageUpload(index, file);
                  }}
                />
                {extra.thumbnail_url && (
                  <div className="mt-2">
                    <img 
                      src={getImagePreviewUrl(extra.thumbnail_url, 'event-extras')} 
                      alt="Extra thumbnail" 
                      className="w-16 h-16 object-cover rounded border"
                      onError={(e) => {
                        console.error('❌ Failed to load extra image:', extra.thumbnail_url);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id={`extra_active_${index}`}
                  checked={extra.is_active}
                  onCheckedChange={(checked) => updateEventExtra(index, { is_active: checked })}
                />
                <Label htmlFor={`extra_active_${index}`}>Active</Label>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addEventExtra}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Event Extra
          </Button>
        </CardContent>
      </Card>

      {/* Coaches External URL */}
      <Card>
        <CardHeader>
          <CardTitle>Coaches Box URL</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="coaches_external_url">Coaches External URL (Optional)</Label>
            <Input
              id="coaches_external_url"
              value={formData.coaches_external_url || ''}
              onChange={(e) => updateFormData({ coaches_external_url: e.target.value })}
              placeholder="https://example.com/coaches-signup"
            />
            <p className="text-xs text-muted-foreground mt-1">
              If provided, the "I will be attending" button will redirect to this URL instead of showing a modal
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExtrasTab;